import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useReduxSelector } from '@hooks/useReduxSelector';

import { arrowArrival, arrowStarting } from "@routes/IconeSvg";
import { employeFlowActions } from "@store/actions";

import "@assets/css/pages/heureTravaillees/heureTravaillees.css";

import DateRangePickerDropdown from "@components/dateRange/DropdownDateRange";
import Button from "@components/tailwindComponents/Button";
import ListBoxWithStatus from "@components/tailwindComponents/ListBoxWithStatus";
import ListboxGroupe from "@components/tailwindComponents/ListboxGroupe/index";
import ListboxSimple from "@components/tailwindComponents/ListboxSimple";
import ListboxEmploye from "@components/tailwindComponents/ListboxEmploye";
import { SvgShowMore } from "@routes/IconeSvg";

import { formatSelectedGroupsForFilter, getInitPeriodFilter, isSamePeriod } from "@helpers/_functions";

const FLUX = [
  {
    _id: "departure",
    name: "Départs",
    arrow: arrowStarting()
  },
  {
    _id: "come",
    name: "Arrivées",
    arrow: arrowArrival()
  }
];

const FilterFlow = ({ setFilterValue, setSuperZIndex }) => {
  const initialPeriod = useMemo(getInitPeriodFilter);
  const { t } = useTranslation("pages.employés.listEmployés");
  const [showFilter, setShowFilter] = useState(false);
  // const [employeeSelected, setEmploye] = useState([]);
  const [selectedType, setSelectedType] = useState([]);
  const [selectedTypeOfDemission, setSelectedTypeOfDemission] = useState([]);

  const dispatch = useDispatch();
  const emplacements = useSelector(({ commonData }) => commonData.emplacements),
    groupes = useSelector(({ commonData }) => commonData.groupes),
    employers = useSelector(({ commonData }) => commonData.employers),
    employeFlowFilter = useSelector(
      ({ onboarding }) => onboarding.employeFlow.filter
    );

  const [selectedEmplacement, setSelectedEmplacement] = useState([]),
    [selectedGroupes, setSelectedGroupes] = useState([]),
    [selectedFlux, setSelectedFlux] = useState(null),
    [employeeSelected, setEmploye] = useState([]),
    [dateSelected, setDateSelected] = useState(initialPeriod);

  const typeDemissionOptions = useReduxSelector(
    ({ onboarding: { typeDemission } }) => typeDemission?.list
  );

  const typeUnavailabilityList = useReduxSelector(({ settings }) => settings.unavailabilityOptions.typeList);
  const typeOptions = useMemo(
    () => (typeUnavailabilityList || []).map(type => ({
      _id: type._id,
      name: type.name
    })),
    [typeUnavailabilityList]
  );

  useEffect(() => {
    if (!isSamePeriod(employeFlowFilter, dateSelected)) {
      setDateSelected(initialPeriod);
      onApplyFilter();
    }
  }, [employeFlowFilter]);

  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const SaveDateSelected = useCallback(([date]) => {
    setDateSelected({
      startDate: date.startDate,
      endDate: date.endDate
    });
  }, []);

  const resetDateSelected = useCallback(
    () => setDateSelected({ startDate: null, endDate: null }),
    []
  );

  const onApplyFilter = () => {
    let periodFilter = dateSelected;
    if (!dateSelected.startDate) {
      periodFilter = initialPeriod;
    }
    dispatch(employeFlowActions.filterData(periodFilter));

    setFilterValue({
      selectedFlux: selectedFlux?._id || "",
      selectedEmplacement: selectedEmplacement.map((e) => e._id),
      selectedGroupes: formatSelectedGroupsForFilter(selectedGroupes),
      employeeSelected: employeeSelected.map((e) => e.id),
      selectedType: selectedType.map((e) => e.name),
      selectedTypeOfDemission: selectedTypeOfDemission.map((e) => e.name)
    });
  };

  const dateRangeValue = useMemo(
    () => ({
      startDate: dateSelected.startDate,
      endDate: dateSelected.endDate
    }),
    [dateSelected]
  );

  return (
    <div className="arh-mb-6 arh-w-full  arh-rounded arh-bg-greyColor arh-p-4">
      <div className="arh-grid arh-grid-cols-5 arh-gap-4">
        <ListBoxWithStatus
          placeholder={t("Flux")}
          name="flux"
          optionList={FLUX}
          selectedValue={selectedFlux}
          setSelectedValue={setSelectedFlux}
          showReset
          noRadio
          toRight={true}
        />

        <ListboxSimple
          placeholder={t("Emplacement")}
          name="emplacement"
          optionList={emplacements}
          selectedValue={selectedEmplacement}
          setSelectedValue={setSelectedEmplacement}
          withSearch
          multiple
          showReset
        />

        <ListboxGroupe
          placeholder={t("Groupes")}
          name="Groupes"
          optionList={groupes}
          selectedValue={selectedGroupes}
          setSelectedValue={setSelectedGroupes}
          withSearch
          showReset
        />

        <div className="datepicker_modals_parcours datePicker_posteAvailable blackSvgColor">
          <div
            className="dateRange_green position_relative DateRangePicker_customisation DateRangePicker_customisation--green">
            <DateRangePickerDropdown
              key="date_range_periode"
              classParent="arhDatePicker"
              idInput="daterange"
              className="input_simple focus_green"
              dateSelected={dateRangeValue}
              textInput={t("Période")}
              SaveCloseRange={SaveDateSelected}
              showResetFilter={Boolean(
                dateSelected.startDate && dateSelected.endDate
              )}
              onResetFilter={resetDateSelected}
              classSwitch="c-switch-pill-checked--green--color--to--left"
              classBackground="green"
              setSuperZIndex={setSuperZIndex}
              isInForeground={true}
            />
          </div>
        </div>
        <div className="arh-flex arh-items-center arh-gap-4">
          <Button onClick={onApplyFilter}>{t("Appliquer")}</Button>
          <Button
            variant="green-outlined"
            onClick={toggleFilter}
            className="!arh-w-fit"
          >
            <SvgShowMore />
          </Button>
        </div>

        <div
          className={`${showFilter ? "arh-grid arh-grid-cols-4 arh-gap-4" : "arh-hidden"} arh-col-span-4`}
        >
          <ListboxEmploye
            placeholder={t("Employé")}
            name="employe"
            optionList={employers || []}
            selectedValue={employeeSelected}
            setSelectedValue={setEmploye}
            multiple
            showReset={Boolean(employeeSelected?.length)}
            customClass="arh-ml-[0.925rem]"
          />
          <ListboxSimple
            placeholder={t("Type")}
            name="cause"
            optionList={typeOptions || []}
            selectedValue={selectedType}

            setSelectedValue={setSelectedType}
            multiple
            showReset={Boolean(selectedType?.length)}
            customClass="arh-ml-[0.925rem]"
          />
          <ListboxSimple
            placeholder={t("Raisons de départ")}
            name="nature"
            optionList={typeDemissionOptions || []}
            selectedValue={selectedTypeOfDemission}
            setSelectedValue={setSelectedTypeOfDemission}
            multiple
            showReset={Boolean(selectedTypeOfDemission?.length)}
            customClass="arh-ml-[0.925rem]"
          />
        </div>
      </div>
    </div>

  );
};

export default memo(FilterFlow);
